import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProductSupplement } from '@next/core-lib/cart';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, skipWhile, take, takeUntil, tap } from 'rxjs/operators';

import { ProductSupplementItemComponent } from './product-supplement-item/product-supplement-item.component';
import { GetVariant, ProductSupplementFormEntry } from '../../models/product-delivery.models';
import { LineItem } from '../../models/product.models';
import { getVariantsRequest } from '../../store/actions';
import * as ProductSelectors from '../../store/selectors';

@Component({
  selector: 'app-product-supplement',
  templateUrl: './product-supplement.component.html',
  styleUrls: ['./product-supplement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, MatIcon, MatDivider, NgFor, ProductSupplementItemComponent, MatButton, MatProgressSpinner, AsyncPipe, AgrI18nSharedModule],
})
export class ProductSupplementComponent implements OnInit, OnDestroy {
  @Output() productSupplementsChange = new EventEmitter<ProductSupplement[]>();
  @Output() productSupplementsIsValid = new EventEmitter<boolean>();
  @Input() lineItem: LineItem;
  @Input() validate = false;

  public productSupplementsFormArray: FormArray = new FormArray([]);
  public isAddBtnDisabled = false;
  public isValid = true;

  public variants$: Observable<GetVariant[]> = this.store.pipe(
    select(ProductSelectors.selectVariants),
    skipWhile((variants) => !variants?.length),
    take(1),
  );
  public variantsLoaded$: Observable<boolean> = this.store.select(ProductSelectors.selectVariantsLoaded);
  public variantsNotAvailable$: Observable<boolean> = this.store.select(ProductSelectors.selectVariantsNotAvailable);

  private destroy$: Subject<void> = new Subject<void>();

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.store.dispatch(getVariantsRequest());

    this.variants$.pipe(take(1)).subscribe((variants) => {
      if (this.lineItem?.productSupplements?.length > 0) {
        this.lineItem.productSupplements.forEach((entry) => {
          const variant = variants.find((variant) => variant.description === entry.description);
          this.productSupplementsFormArray.push(this.getDefaultEntry(variant, entry.quantity));
        });
      } else {
        this.productSupplementsFormArray.push(this.getDefaultEntry());
      }
    });

    this.productSupplementsFormArray.valueChanges
      .pipe(
        debounceTime(100),
        takeUntil(this.destroy$),
        tap(() => {
          this.productSupplementsFormArray.patchValue(
            this.productSupplementsFormArray.value.map(
              (productSupplementForm: ProductSupplementFormEntry): ProductSupplementFormEntry => ({
                ...productSupplementForm,
                quantity:
                  (productSupplementForm.quantity === 0 ||
                    isNaN(productSupplementForm.quantity) ||
                    productSupplementForm.quantity > productSupplementForm.supplement?.maximumQuantity ||
                    productSupplementForm.quantity < productSupplementForm.supplement?.minimumQuantity) &&
                  productSupplementForm.supplement
                    ? productSupplementForm.supplement.recommendedQuantity
                    : productSupplementForm.quantity,
              }),
            ),
            { emitEvent: false },
          );
        }),
      )
      .subscribe(() => {
        this.processSupplementsChange();
      });

    this.emitIsValid();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public addEntry(): void {
    this.productSupplementsFormArray.push(this.getDefaultEntry());
  }

  public compareSupplement(s1: GetVariant, s2: GetVariant): boolean {
    return s1 && s2 ? JSON.stringify(s1) === JSON.stringify(s2) : s1 === s2;
  }

  public getDefaultEntry(variant?: GetVariant, quantity?: number): FormGroup {
    return new FormGroup({
      supplement: new FormControl(variant),
      quantity: new FormControl(quantity || variant?.recommendedQuantity),
    });
  }

  public getFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  public removeEntry(index: number): void {
    this.productSupplementsFormArray.removeAt(index);
  }

  private processSupplementsChange(): void {
    this.productSupplementsChange.emit(this.getSelectedProductSupplements());

    this.emitIsValid();
  }

  private emitIsValid(): void {
    if (this.validate) {
      this.isValid = this.getSelectedProductSupplements().length > 0;
      this.productSupplementsIsValid.emit(this.isValid);
    }
  }

  private getSelectedProductSupplements(): ProductSupplement[] {
    return (this.productSupplementsFormArray.value as ProductSupplementFormEntry[])
      .filter((entry: ProductSupplementFormEntry) => !!entry?.supplement?.description && !!entry?.supplement?.properties)
      .map((entry: ProductSupplementFormEntry) => ({
        description: entry.supplement.description,
        quantity: entry.quantity,
        properties: entry.supplement.properties,
      }));
  }
}
